import { Pencil, Timer } from 'lucide-react';
import { useMutation } from '@platform/api-client';
import { useAuth } from '@platform/auth-client';
import { Button, Skeleton } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { useState } from 'react';
import { useTenantDetail } from '../catalog/useCatalogQueries.ts';
import { LoadFailed } from '../LoadFailed.tsx';
import { usePageContext } from '../usePageContext.ts';
import { SETTINGS_MANAGE_ROLE, type OrderSettingsBody } from './order-settings.ts';
import { OrderSettingsSheet } from './OrderSettingsSheet.tsx';
import { SETTINGS_SECTIONS } from './settings-sections.ts';
import { SettingsSection } from './SettingsSection.tsx';
import styles from './Settings.module.css';

export type OrderSettingsSectionProps = { api: TypedClient };

export const OrderSettingsSection = ({ api }: OrderSettingsSectionProps) => {
  const { t, notify, invalidate, describeError } = usePageContext();
  const canManage = useAuth().hasRole(SETTINGS_MANAGE_ROLE);
  const tenant = useTenantDetail(api);
  const [editing, setEditing] = useState(false);
  const save = useMutation({
    mutationFn: (body: OrderSettingsBody) => api.put('/admin/tenant/order-settings', { body }),
    onSuccess: async () => {
      notify(t('orderSettings.saved'));
      setEditing(false);
      await invalidate('tenant');
    },
  });

  const body = () => {
    if (tenant.isPending) return <Skeleton height="48px" label={t('orderSettings.title')} />;
    if (tenant.isError)
      return (
        <LoadFailed
          description={describeError(tenant.error)}
          onRetry={() => {
            void tenant.refetch();
          }}
        />
      );
    return (
      <>
        <p className={styles['rule']}>
          <span className={styles['ruleValue']}>
            {t('orderSettings.hoursLong', { count: tenant.data.orderConfirmationTtlHours })}
          </span>
          <span>{t('orderSettings.rule')}</span>
        </p>
        {editing ? (
          <OrderSettingsSheet
            hours={tenant.data.orderConfirmationTtlHours}
            submitting={save.isPending}
            errorMessage={save.isError ? (describeError(save.error) ?? null) : null}
            onClose={() => {
              setEditing(false);
            }}
            onSubmit={(values) => {
              save.mutate(values);
            }}
          />
        ) : null}
      </>
    );
  };

  return (
    <SettingsSection
      id={SETTINGS_SECTIONS.orders}
      icon={Timer}
      title={t('orderSettings.title')}
      description={t('orderSettings.context')}
      action={
        canManage && tenant.data !== undefined ? (
          <Button
            icon={<Pencil size={20} aria-hidden="true" />}
            variant="outline"
            onClick={() => {
              save.reset();
              setEditing(true);
            }}
          >
            {t('orderSettings.edit')}
          </Button>
        ) : undefined
      }
    >
      {body()}
    </SettingsSection>
  );
};
